import { ChangeDetectorRef, Component, ElementRef, NgZone, OnInit, signal } from '@angular/core';
import { CanvasRendererBrandingOptions, CanvasRendererContentLoadOptions, CanvasRendererContentType, CanvasRendererLoadDashboardOptions, CanvasRendererLoadVisualOptions, CanvasRendererRenderParams, DashboardDto, DashboardVisualContentDto, EnterpriseUserDataDto, PdfPaperSize, VisualDto } from '../services/api.services';
import { LocalisationService } from '../services/localisation.service';
import { insightsCanvasRendererHostBridge } from './canvas-renderer-host';

@Component({
  selector: 'canvas-renderer',
  templateUrl: './canvas-renderer.component.html',
  styleUrls: ['./canvas-renderer.component.scss'],
  standalone: false
})
export class CanvasRendererComponent implements OnInit {
  readonly CanvasRendererContentType = CanvasRendererContentType;

  options: CanvasRendererContentLoadOptions;

  visualId: number;
  visual: VisualDto;

  dashboardId: number;
  dashboard: DashboardDto;
  dashboardVisualContents: DashboardVisualContentDto[];

  canvasSize: { width?: number, height?: number } = null;
  pageHeight: number | string = '100vh';

  branding: CanvasRendererBrandingOptions = {};
  pages: PageModel[] = [];

  userDataOverride: EnterpriseUserDataDto = null;
  renderUnshared = false;

  forImage = false;

  readyToRender = false;

  readonly locale = signal<string>(null);
  
  constructor(
    private ref: ChangeDetectorRef,
    private ngZone: NgZone,
    private element: ElementRef<HTMLDivElement>,
    public localisationService: LocalisationService
  ) { }

  ngOnInit() {
    insightsCanvasRendererHostBridge.setIsReady(this);
  }

  private updatePages(landscape: boolean, pageCount: number) {
    const pages: PageModel[] = [];

    for (let i = 0; i < pageCount; ++i) {
      pages.push({
        number: i + 1
      });
    }

    this.pages = pages;
  }

  private applyOptions() {
    this.userDataOverride = this.options.userData;
    this.renderUnshared = this.options.renderUnshared;

    if (this.options.contentType == CanvasRendererContentType.Visual) {
      const options = this.options as CanvasRendererLoadVisualOptions;
     
      if (options.visual) {
        this.visualId = null;
        this.visual = options.visual;
        this.readyToRender = true;
      }
      else {
        this.visualId = options.visualId;
        this.visual = null;
        this.readyToRender = true;
      }
    }
    else {
      const options = this.options as CanvasRendererLoadDashboardOptions;

      if (options.dashboard) {
        this.dashboardId = null;
        this.dashboard = options.dashboard;
        this.dashboardVisualContents = options.visualContents;
        this.readyToRender = true;
      }
      else {
        this.dashboardId = options.dashboardId;
        this.dashboard = null;
        this.dashboardVisualContents = null;
        this.readyToRender = true;
      }
    }
  }

  private onRenderParamsChanged(renderParams: CanvasRendererRenderParams) {
    this.updatePages(renderParams.landscape, renderParams.pageCount);

    if (renderParams.renderType == 'image') {
      this.forImage = true;

      this.canvasSize = {
        width: renderParams.size.width,
        height: renderParams.size.height,
      };
    } else {
      this.forImage = false;

      this.canvasSize = null;
    }

    this.locale.set(renderParams.locale);
  }

  private setBranding(brandingOptions: CanvasRendererBrandingOptions) {
    this.branding = brandingOptions;
  }


  private loadContent(options: CanvasRendererContentLoadOptions) {
    this.options = options;

    this.applyOptions();
  }

  onContentRendered() {
    // give some time to all rendering to 'really' finish...
    setTimeout(() => {
      insightsCanvasRendererHostBridge.setContentIsReady(true);
    }, 2000);
  }

  //#region Bridge calls
  public __onRenderParamsChanged(renderParams: CanvasRendererRenderParams) {
    this.ngZone.run(() => {
      this.onRenderParamsChanged(renderParams);
      this.ref.detectChanges();
    });
  }

  public __setBranding(brandingOptions: CanvasRendererBrandingOptions) {
    this.ngZone.run(() => {
      this.setBranding(brandingOptions);
      this.ref.detectChanges();
    });
  }

  public __loadContent(options: CanvasRendererContentLoadOptions) {
    insightsCanvasRendererHostBridge.setContentIsReady(false);

    this.ngZone.run(() => {
      this.loadContent(options);
      this.ref.detectChanges();
    });
  }
  //#endregion
}

interface PageModel {
  number: number;
}
